import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { pedidosAtivosKDS, ActiveOrder } from '../../shared/data/gastronomiaData';
import { Clock, CheckCircle2, ChefHat, RefreshCw, Flame, Store, Bike } from 'lucide-react';

export const GastronomySystem: React.FC<{ prospect: Prospect }> = ({ prospect }) => {
  const [pedidos, setPedidos] = useState<ActiveOrder[]>(pedidosAtivosKDS);

  useEffect(() => {
    window.scrollTo(0, 0);
    const root = document.documentElement;
    root.style.setProperty('--ym-prospect-primary', '#E11D48');
    root.style.setProperty('--ym-prospect-accent', '#F59E0B');
  }, []);

  const advanceStatus = (id: string) => {
    setPedidos(prev => prev.map(p => {
      if (p.id === id) {
        const nextStatus = p.status === 'recebido' ? 'preparo' : p.status === 'preparo' ? 'pronto' : 'saiu_entrega';
        return { ...p, status: nextStatus };
      }
      return p;
    }));
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] font-sans flex flex-col selection:bg-[#E11D48] selection:text-white">
      
      {/* KDS Bar Navigation Clean White - STRICT NO SHADOWS */}
      <header className="h-20 bg-white border-b border-slate-200 px-8 flex items-center justify-between shrink-0">
         <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E11D48] border border-rose-600 text-white flex items-center justify-center font-black">
               <ChefHat className="w-6 h-6" />
            </div>
            <div>
               <h1 className="font-heading font-black text-lg text-[#0F172A] uppercase tracking-wider">KDS Cozinha — {prospect.trade_name}</h1>
               <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Gestão de Pedidos em Tempo Real</p>
            </div>
         </div>

         <div className="flex items-center gap-6">
            <div className="flex gap-4 text-xs font-bold">
               <div className="flex items-center gap-2 bg-amber-50 text-amber-900 border border-amber-200 px-3 py-1.5 rounded-xl">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"></span>
                  <span>{pedidos.filter(p => p.status === 'preparo').length} Na Chapa</span>
               </div>
               <div className="flex items-center gap-2 bg-emerald-50 text-emerald-900 border border-emerald-200 px-3 py-1.5 rounded-xl">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span>
                  <span>{pedidos.filter(p => p.status === 'pronto').length} Prontos</span>
               </div>
            </div>
            <button className="p-2 rounded-xl bg-slate-100 text-slate-600 hover:text-[#0F172A] border border-slate-200"><RefreshCw className="w-5 h-5" /></button>
         </div>
      </header>

      {/* KDS Grid / Order Cards Clean White - STRICT NO SHADOWS */}
      <main className="flex-1 p-8 overflow-x-auto">
         <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {pedidos.map((ped) => {
               const isUrgent = ped.elapsedMinutes > 15;
               return (
                  <div 
                     key={ped.id}
                     className={`bg-white rounded-2xl border ${isUrgent ? 'border-rose-500' : 'border-slate-200'} overflow-hidden flex flex-col justify-between`}
                  >
                     {/* Ticket Header */}
                     <div className={`p-4 ${ped.status === 'preparo' ? 'bg-[#E11D48] text-white' : ped.status === 'pronto' ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white'} flex justify-between items-center`}>
                        <div>
                           <span className="text-xs font-black uppercase tracking-widest">{ped.id}</span>
                           <h3 className="font-bold text-sm leading-tight">{ped.customerName} ({ped.deliveryType})</h3>
                        </div>
                        <div className="flex items-center gap-1 bg-black/20 px-2 py-1 rounded text-[11px] font-bold">
                           <Clock className="w-3 h-3" /> {ped.elapsedMinutes}m
                        </div>
                     </div>

                     {/* Item list */}
                     <div className="p-5 flex-1 space-y-3">
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">Itens do Pedido</span>
                        <ul className="space-y-2">
                           {ped.items.map((item, idx) => (
                              <li key={idx} className="text-xs font-bold text-[#0F172A] flex items-start gap-2 border-b border-slate-100 pb-2">
                                 <span className="text-[#E11D48]">▸</span> {item}
                              </li>
                           ))}
                        </ul>
                     </div>

                     {/* Action Footer */}
                     <div className="p-4 bg-slate-50 border-t border-slate-200">
                        <button 
                           onClick={() => advanceStatus(ped.id)}
                           className={`w-full py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-colors flex items-center justify-center gap-2 border ${
                              ped.status === 'recebido' ? 'bg-amber-500 text-black border-amber-400 hover:bg-amber-400' :
                              ped.status === 'preparo' ? 'bg-emerald-600 text-white border-emerald-700 hover:bg-emerald-700' :
                              'bg-slate-200 text-slate-700 border-slate-300'
                           }`}
                        >
                           {ped.status === 'recebido' && <>Iniciar Preparo <Flame className="w-4 h-4" /></>}
                           {ped.status === 'preparo' && <>Marcar como PRONTO <CheckCircle2 className="w-4 h-4" /></>}
                           {ped.status === 'pronto' && <>Pedido Finalizado ✓</>}
                           {ped.status === 'saiu_entrega' && <>Saiu P/ Entrega</>}
                        </button>
                     </div>
                  </div>
               );
            })}
         </div>
      </main>

    </div>
  );
};
